<template>
    <CompassTableRow
        class="activities-listing-table__row"
        :isSpotlight="isActivitySelected"
        :checkbox="{
            value: `${activity.type}_${activity.activityId}`,
            dataBot: `activities-listing-checkbox__${activity.activityId}`,
        }"
    >
        <template v-for="column in columns">
            <!-- DATE -->
            <CompassTableBodyCell
                v-if="column.code === 'due_date'"
                :key="column.code"
                :class="dateContainerClasses"
            >
                <BadgeActivityStatus
                    v-if="activityIsLate"
                    :tag="tag(activity.type, activity.isLate)"
                />
                <span :class="isBoldDateLabel(activity.type, activity.isLate)">
                    {{ $filters.dateFormatter(activity.date, 'SLASHED_DATE_TIME') }}
                </span>
            </CompassTableBodyCell>
            <!-- TYPE -->
            <CompassTableBodyCell
                v-if="column.code === 'type'"
                :key="column.code"
                minWidth="92px"
                maxWidth="92px"
            >
                <CompassObject :size="sizeObjectType" :object="objectType(activity.type)" />
            </CompassTableBodyCell>

            <!-- PRIORITY -->
            <CompassTableBodyCell
                v-if="column.code === 'priority'"
                :key="column.code"
                minWidth="108px"
                maxWidth="108px"
            >
                <ActivitiesPriorityIconButton
                    :isPending="activity.prioritisation.pending"
                    :isActive="activity.prioritisation.priority > 0"
                    :size="sizePriorityButton"
                    :enabled="activity.acl.can_be_updated"
                    dataBot="activities-listing__priority-icon-button"
                    @clicked="handlePriority"
                />
            </CompassTableBodyCell>

            <!-- LABEL -->
            <CompassTableBodyCell
                v-if="column.code === 'label'"
                :key="column.code"
                minWidth="160px"
                maxWidth="160px"
                class="label__container"
            >
                <CompassTag
                    v-if="activity.label && activity.label.label"
                    type="dot"
                    color="custom"
                    :size="sizeLabelTag"
                    :customColor="getLabelColor(activity.label)"
                >
                    {{ activity.label.label }}
                </CompassTag>
            </CompassTableBodyCell>

            <!-- RELATED -->
            <CompassTableBodyCell
                v-if="column.code === 'related'"
                :key="column.code"
                minWidth="320px"
                maxWidth="580px"
                class="related__container"
                :class="classesRelatedContainer"
            >
                <ActivitiesListingRelatedItem
                    v-for="related in activity.related"
                    :key="`${related.type}_${related.id}`"
                    :data="related"
                    :isActivityPage="isActivityPage"
                />
            </CompassTableBodyCell>
            <!-- DESCRIPTION -->
            <CompassTableBodyCell
                v-if="column.code === 'description'"
                :key="column.code"
                minWidth="480px"
                maxWidth="720px"
                class="description__container"
            >
                <div
                    v-if="activity.description"
                    class="typographies-paragraph-medium-regular"
                    v-html="activity.description"
                />
            </CompassTableBodyCell>
            <!-- TITLE -->
            <CompassTableBodyCell
                v-if="column.code === 'title'"
                :key="column.code"
                minWidth="220px"
                maxWidth="220px"
                class="title__container"
            >
                <span v-if="activity.title" class="typographies-paragraph-medium-regular">
                    {{ activity.title }}
                </span>
            </CompassTableBodyCell>

            <!-- STAFF -->
            <CompassTableBodyCell
                v-if="column.code === 'staff'"
                :key="column.code"
                class="compass-table__cell--auto-width"
            >
                <CompassMultiAvatar :avatars="formattedStaff" />
            </CompassTableBodyCell>
        </template>
        <CompassTableBodyCell
            ref="cell-actions"
            class="activities-listing__cell-actions compass-table__cell--auto-width"
            fixed="right"
            @click.stop
        >
            <ActivitiesListingActions
                ref="wrapper-icon-buttons"
                class="actions__container"
                :activity="activity"
                :isActivityPage="isActivityPage"
                @updateStatus="$emit('updateStatus', { activity })"
                @close="
                    (data) => {
                        $emit('onPanelClosed', data);
                    }
                "
                @updateDueDate="
                    (data) => {
                        $emit('updateDueDate', data);
                    }
                "
            />
        </CompassTableBodyCell>
    </CompassTableRow>
</template>

<script>
import BadgeActivityStatus from 'vcomponents/utilities/badge/BadgeActivityStatus.vue';
import ActivitiesListingRelatedItem from 'vcomponents/activities/listing/ActivitiesListingRelatedItem.vue';
import ActivitiesListingActions from 'vcomponents/activities/listing/ActivitiesListingActions.vue';
import ActivitiesPriorityIconButton from 'vcomponents/activities/priorityIconButton/ActivitiesPriorityIconButton.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapGetters } = createNamespacedHelpers('activitiesStores');
export default {
    components: {
        ActivitiesListingRelatedItem,
        ActivitiesListingActions,
        ActivitiesPriorityIconButton,
        BadgeActivityStatus,
    },
    props: {
        columns: {
            type: Array,
            required: true,
        },
        activity: {
            type: Object,
            required: true,
        },
        isActivityPage: {
            type: Boolean,
            default: false,
        },
        sizeTable: {
            type: String,
            default: 'large',
        },
    },
    data() {
        return {
            labels: {
                owner: this.$t.__('common.labels.owner'),
            },
        };
    },
    computed: {
        ...mapGetters({ metas: 'getMetas', activitySelected: 'getActivitySelected' }),
        isActivitySelected() {
            return (
                this.activitySelected &&
                this.activitySelected.id === this.activity.activityId &&
                this.activitySelected.type === this.activity.type
            );
        },
        sizeObjectType() {
            return this.sizeTable === 'large' ? 'small' : 'extra-small';
        },
        sizeLabelTag() {
            return this.sizeTable === 'large' ? 'medium' : 'small';
        },
        sizePriorityButton() {
            return this.sizeTable === 'large' ? 'medium' : 'small';
        },
        formattedStaff() {
            return this.activity.staff.map((staff) => {
                return {
                    ...staff,
                    fullname:
                        staff.stateBadge === 'owner'
                            ? `${staff.fullname} (${this.$t.__('common.labels.assignee')})`
                            : staff.fullname,
                };
            });
        },
        classesRelatedContainer() {
            return [this.isActivityPage ? '' : 'related__container--dashboard'];
        },
        dateContainerClasses() {
            return ['date__container', this.activityIsLate ? 'date__container--late' : ''];
        },
        activityIsLate() {
            return this.isLate(this.activity.type, this.activity.isLate);
        },
    },
    methods: {
        isEmailOrEvent(type) {
            return ['email', 'event'].includes(type);
        },
        isLate(type, lateness) {
            return !this.isEmailOrEvent(type) && lateness;
        },
        tag(type, lateness) {
            //TODO : email case
            return this.isLate(type, lateness)
                ? { type: 'error', label: this.$l10n.__('En retard') }
                : {};
        },
        isBoldDateLabel(type, lateness) {
            return this.isLate(type, lateness)
                ? 'typographies-paragraph-medium-bold'
                : 'typographies-paragraph-medium-regular';
        },
        objectType(type) {
            switch (type) {
                case 'event':
                    return 'calendar';
                case 'phonecall':
                    return 'phone';
                case 'email':
                    return 'sent-email';
                default:
                    return type;
            }
        },
        getLabelColor(label) {
            if (label.color) {
                return label.color;
            }
            try {
                const {
                    task: { labels },
                } = this.metas;
                const labelFound = labels.find((item) => item.label === label.label);
                return labelFound.color;
            } catch {
                return '#000000';
            }
        },
        handlePriority() {
            this.$emit('updatePriority', {
                priority: this.activity.prioritisation.priority,
                id: this.activity.activityId,
                type: this.activity.type,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.activities-listing-table__row {
    cursor: pointer;
    .date {
        &__container::v-deep {
            .compass-table__cell {
                &-container {
                    min-width: 160px;
                    display: flex;
                    gap: var(--spacing-0);
                    justify-content: center;
                }
            }
        }
        &__container--late::v-deep {
            .compass-table__cell-container {
                align-items: flex-start;
                flex-direction: column;
            }
        }
        &__label {
            display: flex;
        }
    }

    .compass-table__cell--auto-width {
        white-space: nowrap;
        width: 1%;
    }

    .description__container::v-deep {
        p {
            margin: 0;
        }
    }
    .label__container::v-deep .compass-tag__dot {
        flex-shrink: 0;
    }

    .related__container {
        &::v-deep > .compass-table__cell-container {
            flex-wrap: wrap;
            gap: var(--spacing-1);
        }
        &--dashboard::v-deep > .compass-table__cell-container {
            align-items: flex-start;
            flex-direction: column;
            justify-content: center;
            &:has(.activity-listing-related-item-align:only-child) {
                align-items: center;
                flex-direction: row;
            }
        }
    }
}
</style>
